import { toast } from "react-toastify"
import type { RootState } from "@/store"
import { TAGS, api } from "@/store/api.ts"
import { IGameplayActionRequest, IGameplayLog, IGameplayStateResponse } from "."
import { personalDeckEndpoints } from "../personalDeckAPI"
import { gameplayResilience } from "./gameplay.resilience"
import { socketGameplay } from "./gameplay.socket"

const basePath = "/gameplay"

const gameplayAPI = api.enhanceEndpoints({
  addTagTypes: [TAGS.GAMEPLAY, TAGS.DECK]
})

export const gameplayEndpoints = gameplayAPI.injectEndpoints({
  endpoints: (builder) => ({
    getGameplayState: builder.query<
      IGameplayStateResponse,
      { sessionId: string }
    >({
      query: ({ sessionId }) => ({
        url: `${basePath}/state/${sessionId}`,
        method: "GET"
      }),
      providesTags: (_, __, { sessionId }) => [
        { type: TAGS.GAMEPLAY, id: `STATE` },
        { type: TAGS.GAMEPLAY, id: `STATE_${sessionId}` }
      ],
      async onCacheEntryAdded(
        { sessionId },
        { updateCachedData, cacheEntryRemoved, dispatch, getState }
      ) {
        socketGameplay.setUpdateGameplayStateData(updateCachedData)
        socketGameplay.setDispatch(dispatch)

        socketGameplay.init(sessionId)
        socketGameplay.addSub()

        const unsubscribeGameplayUpdate =
          socketGameplay.bindGameplayStateListener()
        const unsubscribeGameplayEvent =
          socketGameplay.bindGameplayEventListener()

        gameplayResilience.start({
          dispatch,
          getState: getState as () => RootState
        })

        try {
          await cacheEntryRemoved
        } finally {
          unsubscribeGameplayUpdate?.()
          unsubscribeGameplayEvent?.()

          gameplayResilience.stop()
          socketGameplay.removeSub()
        }
      }
    }),
    makeGameplayAction: builder.mutation<
      IGameplayStateResponse,
      IGameplayActionRequest
    >({
      query: ({ sessionId, cardId, action }) => ({
        url: `${basePath}/action/${sessionId}`,
        method: "PATCH",
        data: { cardId, action }
      }),
      async onQueryStarted({ cardId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          personalDeckEndpoints.util.updateQueryData(
            "getPersonalDeckInventoryCounts",
            undefined,
            (draft) => {
              if (cardId && draft?.[cardId]) {
                draft[cardId] = draft[cardId] - 1
              }
            }
          )
        )

        try {
          await queryFulfilled
          gameplayResilience.armPostActionWatchdog(cardId)
        } catch {
          patch.undo()
          toast.error("Action failed")
        }
      }
    }),
    getGameplayLastLogs: builder.query<
      IGameplayLog[],
      { sessionId: string; count: number }
    >({
      query: ({ sessionId, count }) => ({
        url: `${basePath}/last-logs/${sessionId}/${count}`,
        method: "GET"
      }),
      providesTags: (_, __, { sessionId, count }) => [
        { type: TAGS.GAMEPLAY, id: `LAST_LOGS` },
        { type: TAGS.GAMEPLAY, id: `LAST_LOGS:${sessionId}:${count}` }
      ]
    })
  })
})

export const {
  useGetGameplayStateQuery,
  useMakeGameplayActionMutation,
  useGetGameplayLastLogsQuery
} = gameplayEndpoints
